// NC1 device enrollment — "Link a device" (§8.4 / AWS-PORT.md chunk 2; v2
// per EPOCH-KEYPAIR.md).
//
// VIEWER requester (a browser WITHOUT the identity): mint an ephemeral X25519
// transfer key plus ephemeral device keys, POST the pre-trust enrollment
// request, display the SAS, poll; once granted, open the sealed bundle,
// verify the received identity against §12 chain custody, and cache it in
// the keystore. The ephemeral keys live in page memory only — a refresh
// abandons the request (it expires server-side; just request again).
//
// Approver (a browser WITH the cached identity):
//   - VIEWER requests: recompute the SAS from the RELAYED keys (never trust
//     the requester's claimed fingerprint — that recomputation plus the human
//     comparison is what defeats a key-swapping relay), then seal the
//     identity secrets to the confirmed transferPk and sign the
//     {requestId, transferPk, sealedBundle} binding.
//   - UPLOADER requests (Windows, v2): nothing is sealed. The approver
//     identifies the machine by its name (the device-add dialog shows the
//     legacy request's name/os/IP alongside) and admits the device's signing
//     key to the chain-committed device list, signing the
//     {requestId, devSignPk} binding. The uploader never receives a secret;
//     its poll returns the device list + member list to verify.
// The server verifies every accept signature under chain custody, so a
// session that merely LOGS IN as the account — but doesn't hold the identity
// — cannot grant anything.

import E2eeApi from '@/aws/e2ee/E2eeApi'
import { getSodium } from './sodium'
import * as Keystore from './Keystore'
import { Nc1UnlockRequiredError } from '../E2EEErrors'
import {
  b64, ub64, computeFingerprint, fingerprintDisplay, deviceListSignedBytes,
} from './shared/envelope.js'
import { canonicalizeBytes } from './shared/jcs.js'
import {
  KIND_DEVICE_LIST, docHashDeviceList, signEntry, entryHash,
  verifyChain, assertDocCommitted,
} from './shared/translog.js'

// The exact bytes the approver's identity key signs when accepting — MUST
// stay byte-identical with netcountable-e2ee/lib/validate.js
// enrollmentAcceptSignedBytes / uploaderAcceptSignedBytes (the server
// verifies under chain custody).
function viewerAcceptSignedBytes({ requestId, transferPk, sealedBundle }) {
  return canonicalizeBytes({ requestId, transferPk, sealedBundle })
}
function uploaderAcceptSignedBytes({ requestId, devSignPk }) {
  return canonicalizeBytes({ requestId, devSignPk })
}

function randomUuid() {
  if (crypto.randomUUID) return crypto.randomUUID()
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, (x) => x.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

// A human-recognizable name for the approver's request list.
function browserDisplayName() {
  const ua = navigator.userAgent || ''
  const browser = /Edg\//.test(ua) ? 'Edge'
    : /OPR\//.test(ua) ? 'Opera'
      : /Firefox\//.test(ua) ? 'Firefox'
        : /Chrome\//.test(ua) ? 'Chrome'
          : /Safari\//.test(ua) ? 'Safari' : 'Browser'
  const os = /Windows/.test(ua) ? 'Windows'
    : /Mac OS X/.test(ua) ? 'macOS'
      : /Android/.test(ua) ? 'Android'
        : /iPhone|iPad/.test(ua) ? 'iOS'
          : /Linux/.test(ua) ? 'Linux' : ''
  return os ? `${browser} on ${os}` : browser
}

// ---- requester side (viewer) -----------------------------------------------

/**
 * Post the pre-trust request and return the poll handle. `sas` is what this
 * browser displays; `transferSk` stays in page memory until the grant.
 */
export async function beginLinkRequest({ email }) {
  const sodium = await getSodium()
  const transfer = sodium.crypto_box_keypair()
  // Viewers mint device keys too: the SAS formula (computeFingerprint)
  // binds transferPk ‖ devSignPk ‖ devEncPk. These never join a device list.
  const devSign = sodium.crypto_sign_keypair()
  const devEnc = sodium.crypto_box_keypair()
  const deviceUuid = randomUuid()
  const fingerprint = computeFingerprint(
    sodium, transfer.publicKey, devSign.publicKey, devEnc.publicKey)

  const resp = (await E2eeApi.createEnrollment({
    email,
    deviceUuid,
    deviceName: browserDisplayName(),
    purpose: 'viewer',
    transferPk: b64(sodium, transfer.publicKey),
    devSignPk: b64(sodium, devSign.publicKey),
    devEncPk: b64(sodium, devEnc.publicKey),
    fingerprint,
  })).data

  return {
    requestId: resp.requestId,
    deviceUuid,
    sas: fingerprintDisplay(fingerprint),
    transferPk: transfer.publicKey,
    transferSk: transfer.privateKey,
  }
}

/**
 * One poll tick. Returns { state: 'pending' | 'rejected' } or, on grant,
 * opens + custody-verifies + caches the identity and returns
 * { state: 'granted' }. Throws when the grant fails verification (treat as
 * fatal for this request — do NOT cache anything).
 */
export async function pollLinkRequest({ email, link }) {
  const resp = (await E2eeApi.pollEnrollment(email, link.deviceUuid)).data
  if (resp.state !== 'granted') {
    return { state: resp.state }
  }

  const sodium = await getSodium()
  const secrets = JSON.parse(new TextDecoder().decode(sodium.crypto_box_seal_open(
    ub64(sodium, resp.sealedBundle), link.transferPk, link.transferSk)))

  // The seal proves the bundle was addressed to OUR transfer key; chain
  // custody proves the identity inside is the account's real one. Verify
  // before caching — a compromised relay must not be able to hand us a
  // substitute identity.
  const probe = (await E2eeApi.getAccount(email)).data
  if (!probe.altAccountId) throw new Error('account has no altAccountId')
  const log = (await E2eeApi.getLog(email)).data
  const chain = verifyChain(sodium, probe.altAccountId, log, null)
  if (secrets.id_sign_pk !== chain.head.signerPk) {
    throw new Error('received identity does not match chain custody')
  }

  await Keystore.saveIdentity(email, { secrets, altAccountId: probe.altAccountId, email })
  sodium.memzero(link.transferSk)
  console.log(`nc1 device link complete for ${email}`)
  return { state: 'granted' }
}

// ---- approver side ----------------------------------------------------------

/**
 * deviceUuids of the account's still-pending LEGACY device-add requests
 * (`account.account.addDeviceRequest` in the store). Each request's
 * identityId is the REVERSED composite `cognitoId|deviceUUID` — un-reverse
 * to recover the deviceUuid (the decoding AddDeviceRequestDialog uses).
 *
 * Used to split uploader (app/device) NC1 enrollments between the two
 * approval surfaces: while the legacy request is pending the dashboard's
 * "Add device request" dialog approves both halves together; once the
 * legacy half has been granted, only /link-device can finish the NC1 half
 * (E2EE campaign finding ①, §2.6).
 *
 * @param {*} storeAccount `$store.state.account` (may be null before load)
 * @returns {Set<string>}
 */
export function legacyPendingDeviceUuids(storeAccount) {
  const out = new Set()
  const reqs = storeAccount && storeAccount.account && storeAccount.account.addDeviceRequest
  if (!Array.isArray(reqs)) return out
  for (const r of reqs) {
    const raw = [...String((r && r.identityId) || '')].reverse().join('')
    const uuid = raw.split('|')[1]
    if (uuid) out.add(uuid)
  }
  return out
}

/**
 * Pending requests for the account. Viewer requests carry the SAS recomputed
 * from the relayed keys and a claimMismatch flag (the requester's claimed
 * fingerprint disagreeing with the relayed keys = likely tampering — the UI
 * must warn). Uploader requests (v2) carry no SAS: `sas` is null and the UI
 * identifies them by device name.
 */
export async function listPendingRequests(email) {
  const list = (await E2eeApi.listEnrollments(email)).data
  const sodium = await getSodium()
  return list
    .filter((e) => e.state === 'pending')
    .map((e) => {
      if (e.purpose === 'uploader' || !e.transferPk) {
        return { ...e, sas: null, claimMismatch: false }
      }
      const fingerprint = computeFingerprint(
        sodium, ub64(sodium, e.transferPk), ub64(sodium, e.devSignPk), ub64(sodium, e.devEncPk))
      return {
        ...e,
        sas: fingerprintDisplay(fingerprint),
        claimMismatch: !!e.fingerprint && e.fingerprint !== fingerprint,
      }
    })
}

/**
 * Approve `request`.
 *
 * Viewer: after the human verified the code shown on the requesting browser
 * — either by typing it (`enteredCode`) or, since the 2026-08-13
 * compare-don't-type UX decision, by visually comparing the displayed SAS
 * with the device's screen and explicitly confirming (`visuallyConfirmed`).
 * The displayed SAS is always recomputed from the RELAYED keys, never the
 * requester's claim, so a substituted key still shows two different codes.
 *
 * Uploader (v2): no code. The caller identified the machine by name; this
 * admits its signing key to the chain-committed device list.
 */
export async function approveRequest({ email, request, enteredCode, visuallyConfirmed }) {
  const sodium = await getSodium()
  const cached = await Keystore.loadIdentity(email)
  if (!cached || !cached.secrets) {
    // Typed error → the callers route it to the Nc1UnlockDialog.
    throw new Nc1UnlockRequiredError(email)
  }
  const idSignSk = ub64(sodium, cached.secrets.id_sign_sk)

  if (request.purpose === 'uploader') {
    // §12: never extend an unverified device list — the transparency chain
    // must commit exactly the list we fetched, and our cached identity must
    // still be the custody key.
    const deviceList = (await E2eeApi.getDeviceList(email)).data
    const log = (await E2eeApi.getLog(email)).data
    const chain = verifyChain(sodium, cached.altAccountId, log, null)
    assertDocCommitted('device-list', chain.latest.deviceList,
      deviceList.version, docHashDeviceList(sodium, deviceList))
    if (cached.secrets.id_sign_pk !== chain.head.signerPk) {
      // The account's identity ROTATED since this browser cached its copy.
      // The stale identity is useless — clear it so every code path treats
      // this browser as locked, and route to the unlock dialog.
      await Keystore.clearIdentity(email)
      throw new Nc1UnlockRequiredError(email)
    }

    const value = {
      userId: deviceList.userId,
      version: deviceList.version + 1,
      devices: [
        ...deviceList.devices.filter((d) => d.deviceId !== request.deviceUuid),
        {
          deviceId: request.deviceUuid,
          devSignPk: request.devSignPk,
          addedAt: Date.now(),
        },
      ],
    }
    value.identitySig = b64(sodium,
      sodium.crypto_sign_detached(deviceListSignedBytes(value), idSignSk))
    const logEntry = signEntry(sodium, {
      accountId: cached.altAccountId,
      seq: log.length,
      prevHash: entryHash(sodium, log[log.length - 1]),
      ts: Date.now(),
      kind: KIND_DEVICE_LIST,
      docVersion: value.version,
      docHash: docHashDeviceList(sodium, value),
      signerPk: cached.secrets.id_sign_pk,
    }, idSignSk)
    const sig = b64(sodium, sodium.crypto_sign_detached(
      uploaderAcceptSignedBytes({ requestId: request.requestId, devSignPk: request.devSignPk }), idSignSk))

    await E2eeApi.acceptEnrollment(email, request.requestId, { email, sig, deviceList: value, logEntry })
    return
  }

  const expected = computeFingerprint(sodium,
    ub64(sodium, request.transferPk), ub64(sodium, request.devSignPk), ub64(sodium, request.devEncPk))
  if (visuallyConfirmed === true) {
    // No typed cross-check in this mode, so an inconsistent claimed
    // fingerprint (requester's claim vs the relayed keys) is a hard stop —
    // it means the request was altered in transit.
    if (request.fingerprint && request.fingerprint !== expected) {
      throw new Error('This request\'s claimed code does not match its keys — do not approve '
        + '(the request may have been tampered with).')
    }
  } else {
    const entered = String(enteredCode || '').replace(/[-\s]/g, '').toLowerCase()
    if (entered !== expected) {
      throw new Error('The code you typed does not match this request. If it matches the '
        + 'other device\'s screen exactly, do not approve — the request may have been tampered with.')
    }
  }

  const sealedBundle = b64(sodium, sodium.crypto_box_seal(
    new TextEncoder().encode(JSON.stringify(cached.secrets)), ub64(sodium, request.transferPk)))
  const sig = b64(sodium, sodium.crypto_sign_detached(
    viewerAcceptSignedBytes({ requestId: request.requestId, transferPk: request.transferPk, sealedBundle }),
    idSignSk))
  await E2eeApi.acceptEnrollment(email, request.requestId, { email, sealedBundle, sig })
}

export async function rejectRequest({ email, requestId }) {
  await E2eeApi.rejectEnrollment(email, requestId)
}
